import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { SettingsTabBar } from '../../components/settings/SettingsTabBar';
import { SettingsTabItem } from '../../components/settings/SettingsTabItem';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { fetchHangfireDashboardUrl } from '../../services/monitorApi';
import { MonitorTabId } from './MonitorTabId';

const MONITOR_TABS: SettingsTabItem<MonitorTabId>[] = [
  { id: 'tasks', label: 'Tarefas' },
  { id: 'database', label: 'Banco de Dados' },
  { id: 'apis', label: 'APIs' },
];

function isLoopbackHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

function resolveEmbeddedHangfireUrl(apiDashboardUrl: string): string {
  try {
    const parsed = new URL(apiDashboardUrl);
    const token = parsed.searchParams.get('access_token');
    const pageHost = window.location.hostname;
    if (isLoopbackHost(parsed.hostname) && !isLoopbackHost(pageHost) && token) {
      return `${window.location.origin}/hangfire/?access_token=${encodeURIComponent(token)}`;
    }

    return apiDashboardUrl;
  } catch {
    return apiDashboardUrl;
  }
}

function resolveDashboardError(error: unknown): string {
  if (isAxiosError(error)) {
    if (error.response?.status === 403) {
      return 'Acesso negado. O dashboard Hangfire exige usuário com perfil Administrador.';
    }

    if (error.response?.status === 401) {
      return 'Sessão expirada. Faça login novamente.';
    }

    if (error.code === 'ERR_NETWORK') {
      return 'Não foi possível contatar a API. Verifique se o backend está em execução.';
    }
  }

  return 'Não foi possível carregar o dashboard de tarefas.';
}

export function MonitorPage() {
  const [activeTab, setActiveTab] = useState<MonitorTabId>('tasks');
  const [dashboardUrl, setDashboardUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeTab !== 'tasks') {
      return;
    }

    let cancelled = false;

    async function loadDashboard(): Promise<void> {
      setLoading(true);
      setErrorMessage(null);
      try {
        const result = await fetchHangfireDashboardUrl();
        if (!cancelled) {
          setDashboardUrl(resolveEmbeddedHangfireUrl(result.dashboardUrl));
        }
      } catch (error) {
        if (!cancelled) {
          setDashboardUrl(null);
          const message = resolveDashboardError(error);
          setErrorMessage(message);
          toast.error(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadDashboard();

    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  return (
    <PageShell>
      <PageHeader
        title="Monitor"
        description="Tarefas em background, saúde do banco e APIs."
      />

      <SettingsTabBar tabs={MONITOR_TABS} activeTab={activeTab} onChange={setActiveTab} />

      <div className="mt-4">
        {activeTab === 'tasks' ? (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            {loading ? (
              <div className="p-8">
                <LoadingSpinner label="Carregando dashboard Hangfire..." />
              </div>
            ) : dashboardUrl ? (
              <>
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2 text-sm text-slate-500">
                  <span>Worker Hangfire embutido</span>
                  <a
                    href={dashboardUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Abrir em nova aba
                  </a>
                </div>
                <iframe
                  title="Dashboard Hangfire"
                  src={dashboardUrl}
                  className="h-[min(80vh,900px)] w-full border-0"
                />
              </>
            ) : (
              <div className="space-y-2 p-8 text-center text-sm text-slate-500">
                <p>{errorMessage ?? 'Dashboard indisponível.'}</p>
                <p>
                  O dashboard Hangfire exige perfil Administrador. Em produção ele é embutido em
                  /hangfire no mesmo endereço da aplicação.
                </p>
              </div>
            )}
          </div>
        ) : null}

        {activeTab === 'database' ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center shadow-sm">
            <h3 className="text-lg font-semibold text-slate-800">Monitoramento de banco de dados</h3>
            <p className="mt-2 text-sm text-slate-500">
              Em breve: métricas de conexões, latência, espaço e saúde das instâncias PostgreSQL.
            </p>
          </div>
        ) : null}

        {activeTab === 'apis' ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center shadow-sm">
            <h3 className="text-lg font-semibold text-slate-800">Monitoramento de APIs</h3>
            <p className="mt-2 text-sm text-slate-500">
              Em breve: dashboards Prometheus e Grafana para latência, erros e throughput das APIs.
            </p>
          </div>
        ) : null}
      </div>
    </PageShell>
  );
}
